import React, { useRef, useState } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { Tag } from 'primereact/tag';
import moment from 'moment';
import html2canvas from 'html2canvas';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';

import ApiService from '../services/ApiService';
import { useAuth } from '../context/auth';
import CouponCard from './CouponCard';

const ActiveCodes = ({
	setCode,
	codes,
	setCodes,
	revokedCodes,
	setRevokedCodes,
	filters,
	getSeverity,
	descriptionBodyTemplate,
	toast,
	onGlobalFilterChange,
	globalFilterValue,
	setPromotion,
	setSubmittedPromotion,
	setSubmittedCode,
	setPromotionDialog,
	setCodeDialog,
	emptyPromotion,
	emptyCode,
	setCodeDialogTitle,
}) => {
	const api = new ApiService();
	const { authTokens } = useAuth();
	const headers = {
		'X-Firebase-Auth-Client': authTokens.token,
	};

	const [selectedCodes, setSelectedCodes] = useState([]);
	const [hiddenRenderCodes, setHiddenRenderCodes] = useState([]);
	const exportRootRef = useRef(null);
	const [exportingId, setExportingId] = useState(null);
	const [isBulkExporting, setIsBulkExporting] = useState(false);

	const COUPON_TEMPLATE_URL = '/images/coupon-ticket.png';

	const slugify = (s) =>
		String(s || '')
			.normalize('NFKD')
			.replace(/[^\w\s-]/g, '')
			.trim()
			.replace(/\s+/g, '-')
			.toLowerCase();

	const openNewPromotion = () => {
		setPromotion(emptyPromotion);
		setSubmittedPromotion(false);
		setPromotionDialog(true);
	};

	const openNewCode = () => {
		setCode(emptyCode);
		setSubmittedCode(false);
		setCodeDialog(true);
		setCodeDialogTitle('Create Code');
	};

	const editCode = (rowData) => {
		const formattedStartTime = moment(rowData.startTime, 'DD-MM-YYYY HH:mm').toDate();
		const formattedEndTime = moment(rowData.endTime, 'DD-MM-YYYY HH:mm').toDate();

		const objForEditCode = {
			id: rowData.id,
			name: rowData.name,
			description: rowData.description,
			maxClaims: rowData.maxClaims,
			maxClaimsPerUser: rowData.maxClaimsByUser,
			startTime: formattedStartTime,
			endTime: formattedEndTime,
			promotionId: rowData.promotion.id,
		};

		setCode(objForEditCode);
		setPromotion(rowData.promotion);
		setSubmittedCode(true);
		setCodeDialog(true);
		setCodeDialogTitle('Edit Code');
	};

	const revokeCode = async (code, index) => {
		try {
			await api.revokeCode(code.id, headers);

			let _codes = [...codes];
			_codes.splice(index, 1);

			setCodes(_codes);

			let _revokedCodes = [...revokedCodes];
			_revokedCodes.push(code);

			setRevokedCodes(_revokedCodes);

			toast.current.show({
				severity: 'success',
				summary: 'Success',
				detail: 'Code successfully revoked',
				life: 3000,
			});
		} catch (error) {
			console.log(error);

			toast.current.show({
				severity: 'error',
				summary: 'Error',
				detail: 'Something went wrong while revoking the code',
				life: 3000,
			});
		}
	};

	const renderNextTick = () => new Promise((r) => setTimeout(r, 60));

	const rasterizeNodeToBlob = async (node) =>
		new Promise(async (resolve, reject) => {
			try {
				// Ensure web fonts are ready so html2canvas captures the correct glyphs
				if (document && document.fonts && document.fonts.ready) {
					try {
						await document.fonts.ready;
					} catch (e) {
						// ignore font readiness errors and continue
					}
				}
				const canvas = await html2canvas(node, {
					useCORS: true,
					backgroundColor: null,
					scale: 2,
				});
				canvas.toBlob((blob) => {
					if (!blob) return reject(new Error('Failed to create blob'));
					resolve(blob);
				}, 'image/png');
			} catch (e) {
				reject(e);
			}
		});

	const exportSingle = async (code) => {
		try {
			setExportingId(code.id);
			setHiddenRenderCodes([code]);
			await renderNextTick();

			const node = document.getElementById(`coupon-${code.id}`);
			if (!node) throw new Error('Coupon DOM not found for export');
			const blob = await rasterizeNodeToBlob(node);
			const filename = `coupon-${slugify(code.name || 'code')}-${code.id}.png`;
			saveAs(blob, filename);
		} catch (e) {
			console.error(e);
			if (toast && toast.current && typeof toast.current.show === 'function') {
				toast.current.show({
					severity: 'error',
					summary: 'Error',
					detail: 'Exporting coupon failed.',
					life: 3000,
				});
			}
		} finally {
			setHiddenRenderCodes([]);
			setExportingId(null);
		}
	};

	const exportSelected = async () => {
		if (!selectedCodes?.length) return;
		try {
			setIsBulkExporting(true);
			setHiddenRenderCodes(selectedCodes);
			await renderNextTick();

			const zip = new JSZip();
			for (const code of selectedCodes) {
				const node = document.getElementById(`coupon-${code.id}`);
				if (!node) continue;
				const blob = await rasterizeNodeToBlob(node);
				const filename = `coupon-${slugify(code.name || 'code')}-${code.id}.png`;
				zip.file(filename, blob);
			}

			const content = await zip.generateAsync({ type: 'blob' });
			saveAs(content, `coupons-${Date.now()}.zip`);

			if (toast && toast.current && typeof toast.current.show === 'function') {
				toast.current.show({
					severity: 'success',
					summary: 'Success',
					detail: 'Selected coupons are exported.',
					life: 2500,
				});
			}
		} catch (e) {
			console.error(e);
			if (toast && toast.current && typeof toast.current.show === 'function') {
				toast.current.show({
					severity: 'error',
					summary: 'Error',
					detail: 'Exporting selection failed.',
					life: 3000,
				});
			}
		} finally {
			setHiddenRenderCodes([]);
			setIsBulkExporting(false);
		}
	};

	const header = (
		<div className="flex align-items-center justify-content-between">
			<span className="p-input-icon-left">
				<i className="pi pi-search" />
				<InputText value={globalFilterValue} onChange={onGlobalFilterChange} placeholder="Keyword Search" />
			</span>

			<div className="flex align-items-center justify-content-end">
				<Button
					label="Export selected"
					icon={isBulkExporting ? 'pi pi-spin pi-spinner' : 'pi pi-download'}
					className="mr-2"
					disabled={!selectedCodes?.length || isBulkExporting || exportingId !== null}
					onClick={exportSelected}
					aria-label="Export selected coupons to ZIP"
				/>
				<Button label="Create Code" icon="pi pi-plus" className="mr-2" onClick={openNewCode} />
				<Button label="Add Promotion" icon="pi pi-plus" severity="secondary" outlined onClick={openNewPromotion} />
			</div>
		</div>
	);

	return (
		<>
			<DataTable
				value={codes}
				paginator
				rows={10}
				rowsPerPageOptions={[10, 25, 50]}
				removableSort
				filters={filters}
				emptyMessage="No codes found"
				header={header}
				tableStyle={{ minWidth: '60rem' }}
				globalFilterFields={[
					'name',
					'description',
					'maxClaims',
					'maxClaimsByUser',
					'startTime',
					'endTime',
					'totalClaimed',
					'promotion?.type',
				]}
				size="small"
				currentPageReportTemplate="Showing {first} to {last} of {totalRecords} products"
				paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
				selection={selectedCodes}
				onSelectionChange={(e) => setSelectedCodes(e.value)}
				dataKey="id"
			>
				<Column selectionMode="multiple" headerStyle={{ width: '3rem' }} exportable={false}></Column>
				<Column
					field="name"
					header="Codes"
					sortable
					style={{ minWidth: '3rem' }}
					body={descriptionBodyTemplate}
				></Column>
				<Column field="maxClaims" header="Max" sortable style={{ minWidth: '3rem' }}></Column>
				<Column field="maxClaimsByUser" header="Max per user" sortable style={{ minWidth: '5rem' }}></Column>
				<Column field="startTime" header="Start date" sortable style={{ minWidth: '5rem' }}></Column>
				<Column field="endTime" header="End date" sortable style={{ minWidth: '5rem' }}></Column>
				<Column field="totalClaimed" header="Total claimed" sortable style={{ minWidth: '5rem' }}></Column>
				<Column
					field="promotion.type"
					header="Promotion"
					sortable
					style={{ minWidth: '5rem' }}
					body={(rowData) => <Tag severity={getSeverity(rowData.promotion?.type)}>{rowData.promotion?.type}</Tag>}
				></Column>
				<Column
					field="id"
					body={(rowData) => (
						<div className="flex align-items-center justify-content-center">
							<Button
								rounded
								text
								size="small"
								icon="pi pi-pencil"
								className="p-button-warning"
								style={{ marginRight: '1rem', padding: '0.4rem', height: '1.4rem', width: '1.4rem' }}
								onClick={() => editCode(rowData)}
							/>
							<Button
								rounded
								text
								size="small"
								icon={exportingId === rowData.id ? 'pi pi-spin pi-spinner' : 'pi pi-download'}
								className="p-button-help"
								style={{ marginRight: '1rem', padding: '0.4rem', height: '1.4rem', width: '1.4rem' }}
								onClick={() => exportSingle(rowData)}
								disabled={isBulkExporting || exportingId !== null}
								aria-label="Export coupon as PNG"
							/>
							<Button
								rounded
								text
								size="small"
								icon="pi pi-trash"
								className="p-button-danger"
								style={{ padding: '0.4rem', height: '1.4rem', width: '1.4rem' }}
								onClick={() => revokeCode(rowData)}
							/>
						</div>
					)}
					style={{ minWidth: '8rem' }}
				></Column>
			</DataTable>

			<div
				ref={exportRootRef}
				className="fixed"
				style={{ left: '-10000px', top: 0, width: 0, height: 0, overflow: 'hidden' }}
				aria-hidden="true"
			>
				{hiddenRenderCodes.map((c) => (
					<div key={c.id} className="mb-3">
						<CouponCard code={c} templateUrl={COUPON_TEMPLATE_URL} width={600} height={650} />
					</div>
				))}
			</div>
		</>
	);
};

export default ActiveCodes;
